import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Clock, MapPin, Trash2 } from "lucide-react";

export default function Calendarr() {
  const [date, setDate] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", service_id: "", pet_id: "", time: "10:00", notes: "", address: "" });

  const load = async () => {
    const [{ data: a }, { data: s }, { data: c }] = await Promise.all([
      api.get("/appointments"), api.get("/services"), api.get("/clients"),
    ]);
    setAppts(a); setServices(s); setClients(c);
  };
  useEffect(() => { load(); }, []);

  const dayAppts = appts.filter(a => new Date(a.start).toDateString() === date.toDateString())
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  const submit = async () => {
    if (!form.client_id || !form.service_id) { toast.error("Client and service required"); return; }
    const [h, m] = form.time.split(":");
    const dt = new Date(date); dt.setHours(+h, +m, 0, 0);
    try {
      await api.post("/appointments", {
        client_id: form.client_id,
        service_id: form.service_id,
        pet_id: form.pet_id || null,
        start: dt.toISOString(),
        address: form.address,
        notes: form.notes,
      });
      toast.success("Appointment booked");
      setOpen(false);
      setForm({ client_id: "", service_id: "", pet_id: "", time: "10:00", notes: "", address: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
  };

  const cancel = async (a) => {
    const hasSeries = !!a.recurring_series_id;
    let scope = "one";
    if (!window.confirm(hasSeries ? "Cancel this appointment?" : "Cancel this appointment?")) return;
    if (hasSeries) {
      const wantSeries = window.confirm(
        "This is part of a recurring series. Click OK to cancel the WHOLE remaining series, or Cancel to only cancel this one visit."
      );
      scope = wantSeries ? "series" : "one";
    }
    try {
      await api.delete(`/appointments/${a.id}?scope=${scope}`);
      toast.success(scope === "series" ? "Series cancelled" : "Appointment cancelled");
      load();
    } catch { toast.error("Cancel failed"); }
  };

  const selectedClient = clients.find(c => c.id === form.client_id);
  const clientPets = selectedClient?.pets || [];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">Schedule</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Calendar</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile" data-testid="btn-new-appt">
              <Plus className="w-4 h-4 mr-1"/> New appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display">Book a stop</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, pet_id: "" })}>
                  <SelectTrigger data-testid="select-client"><SelectValue placeholder="Choose client"/></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {clientPets.length > 0 && (
                <div>
                  <Label>Pet</Label>
                  <Select value={form.pet_id} onValueChange={(v) => setForm({ ...form, pet_id: v })}>
                    <SelectTrigger data-testid="select-pet"><SelectValue placeholder="Choose pet"/></SelectTrigger>
                    <SelectContent>
                      {clientPets.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.breed}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Service</Label>
                <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                  <SelectTrigger data-testid="select-service"><SelectValue placeholder="Choose service"/></SelectTrigger>
                  <SelectContent>
                    {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ${s.price} · {s.duration_min}m</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input value={date.toLocaleDateString()} disabled/>
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="input-time"/>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Leave blank to use client address" data-testid="input-address"/>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-notes"/>
              </div>
              <Button onClick={submit} className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="btn-submit-appt">
                Book
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-[#E5DCCE] p-5 shadow-sm">
          <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)}
            className="rounded-xl" data-testid="calendar"/>
        </div>
        <div className="lg:col-span-3 bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm">
          <h3 className="font-display text-xl font-bold mb-4">
            {date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </h3>
          {dayAppts.length === 0 ? (
            <p className="text-sm text-[#6B625B]">No appointments on this day.</p>
          ) : (
            <ul className="space-y-3" data-testid="day-list">
              {dayAppts.map(a => (
                <li key={a.id} className="rounded-2xl border border-[#E5DCCE] p-4 flex items-start gap-4">
                  <div className="w-16 text-center">
                    <div className="font-display font-bold">
                      {new Date(a.start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </div>
                    <div className="text-[10px] text-[#6B625B] flex items-center justify-center gap-1">
                      <Clock className="w-3 h-3"/> {a.duration_min}m
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{a.client_name} · {a.pet_name || "—"}</div>
                    <div className="text-xs text-[#6B625B]">{a.service_name} · ${a.price}</div>
                    <div className="text-xs text-[#6B625B] flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3"/> {a.address || "—"}
                    </div>
                    {a.notes && <div className="text-xs mt-2 bg-[#F5E6CF] rounded-lg p-2">{a.notes}</div>}
                  </div>
                  <span className={`pill border text-[10px] ${
                    a.status === "cancelled" ? "bg-rose-50 text-rose-700 border-rose-200" :
                    a.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>{a.status.replace("_"," ")}</span>
                  {a.status !== "cancelled" && (
                    <Button size="icon" variant="ghost" onClick={() => cancel(a)}
                      className="text-rose-600" data-testid={`cancel-${a.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
